import { useMemo } from "react";
import { formatValue, asNumber, toneColor } from "../../lib/kernelFormat.js";
import { sanitizeRenderColor } from "../../lib/renderSafety.js";
import { KernelEmpty } from "./shared.jsx";

export const kind = "funnel";

// Stage-ordered drop-off. Bars are center-aligned and shrink in data
// order so the diagram reads as a literal funnel even within our
// rectangular aesthetic. The inter-stage callout — absolute drop +
// kept % — is the real "funnel" insight; the per-stage values are
// secondary, so we draw them under the bar in muted type.
//
// Tone-thresholds on the drop callout: drops of >10% read as "bad"
// (red), 3-10% as "muted", and <3% as "good" — so a funnel with a
// cliff is visually obvious without the user having to read numbers.
export function Component({ spec, data }) {
    const stages = useMemo(() => {
        const arr = Array.isArray(data) ? data : [];
        return arr.map((row) => ({
            label: row?.[spec.labelField] ?? "",
            value: asNumber(row?.[spec.valueField]) ?? 0,
        }));
    }, [data, spec.labelField, spec.valueField]);

    if (!stages.length) return <KernelEmpty message={spec.empty || "no stages"} />;

    const first = stages[0].value || 0;
    const color = sanitizeRenderColor(spec.color, "var(--green)");
    const fmt = spec.format || "number";

    return (
        <div className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[14px] py-[12px]">
            {stages.map((stage, i) => {
                const ofFirst = first > 0 ? stage.value / first : 0;
                const widthPct = Math.max(2, Math.min(100, ofFirst * 100));
                const prev = i > 0 ? stages[i - 1].value : null;
                const drop = prev != null ? prev - stage.value : null;
                const keptPct = prev != null && prev > 0 ? stage.value / prev : null;
                const dropPct = keptPct != null ? 1 - keptPct : null;
                const dropTone = dropPct == null ? "muted"
                    : dropPct > 0.10 ? "bad"
                    : dropPct > 0.03 ? "muted"
                    : "good";
                return (
                    <div key={i}>
                        {/* inter-stage callout */}
                        {drop != null ? (
                            <div className="flex items-center justify-center gap-[8px] py-[4px] text-[10px] leading-[1] text-[color:var(--muted-soft)]">
                                <span className="font-mono">↓</span>
                                <span
                                    className="font-mono tabular-nums"
                                    style={{ color: toneColor(dropTone) }}
                                >
                                    {drop > 0 ? "−" : "+"}{formatValue(Math.abs(drop), fmt)}
                                </span>
                                <span className="text-[color:var(--muted-soft)]">
                                    ({(keptPct * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}% kept)
                                </span>
                            </div>
                        ) : null}

                        {/* stage block — title, centered bar, value */}
                        <div className="flex flex-col items-center">
                            <div className="text-[11px] font-medium leading-[1.2] text-[color:var(--ink)] text-center">
                                {String(stage.label)}
                            </div>
                            <div className="relative mt-[4px] flex h-[18px] w-full items-center justify-center">
                                {/* stippled fill: radial-gradient dot
                                    grid pinned to 8px / 1.1px to match
                                    the canvas charts' fillDotsInPath. */}
                                <div
                                    className="h-full rounded-[1px] border"
                                    style={{
                                        width: `${widthPct}%`,
                                        color,
                                        backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 0.8px, transparent 1.2px)",
                                        backgroundSize: "5px 5px",
                                        backgroundColor: "transparent",
                                        borderColor: color,
                                        opacity: 0.95 - i * 0.05,
                                    }}
                                />
                            </div>
                            <div className="mt-[3px] text-[10px] font-mono tabular-nums text-[color:var(--muted)]">
                                {formatValue(stage.value, fmt)}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
